/**
 * CUSTOM KIYO — kiyo-scroll-bridge.js
 *
 * Lets third-party scripts see the page scroll again on desktop.
 *
 * Heritage 4.1.3 scrolls `.page-wrapper` on viewports >= 990px; html and body
 * are overflow: hidden (base.css, scroll-container.js). Every third-party
 * script that measures reading depth still watches the WINDOW:
 *   - Klaviyo form trigger SCROLL_PERCENTAGE: window "scroll" listener,
 *     pageYOffset / (max(body|html scrollHeight, offsetHeight, clientHeight)
 *     - innerHeight) — sharedUtils module 58696, verified 2026-09-02,
 *   - Klaviyo exit intent and engaged-session telemetry (same helper),
 *   - Intelligems custom events "Scroll Depth N": window "scroll",
 *     (scrollY + innerHeight) / documentElement.scrollHeight.
 * On desktop the window never scrolls and the document is one viewport tall,
 * so these either never fire or fire at load. Details: THEME-CHANGES.md
 * 2026-09-02 (audit findings 01 and 02).
 *
 * This bridge, active only while `.page-wrapper` is the scroll container
 * (same media query as scroll-container.js):
 *   1. window.scrollY / window.pageYOffset and the scrollTop of <html> and
 *      <body> report (and set) the wrapper's scrollTop,
 *   2. scrollHeight of <html> and <body> reports the wrapper's scrollHeight,
 *   3. every wrapper "scroll" event is re-dispatched as a document-level
 *      "scroll" event, which bubbles to window listeners like a native one.
 * Below 990px, or without a wrapper, every accessor falls back to the native
 * getter and nothing is relayed. The theme's own code is untouched either
 * way: on desktop it reads the wrapper directly (scroll-container.js);
 * document.scrollingElement, scrollTo/scrollBy and scroll restoration are
 * not shimmed. kiyo-scroll-depth.js (GA4) reads the wrapper itself.
 *
 * Deliberately NOT done: forcing the window to scroll again (would fight
 * scroll-container.js: header, scroll restore, dialogs) or editing each
 * tool's trigger settings (no API for Klaviyo form triggers, and it would
 * only move the problem).
 *
 * Fail-safe: a property that cannot be redefined keeps its native behaviour;
 * nothing else on the page depends on this file.
 */
(() => {
  if (window.__kiyoScrollBridge) return;
  window.__kiyoScrollBridge = true;

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const wrapper = () => document.querySelector('.page-wrapper');
  // Same rule as scroll-container.js: the wrapper scrolls only from 990px up.
  const scroller = () => (desktopQuery.matches ? wrapper() : null);

  const own = (target, name) => Object.getOwnPropertyDescriptor(target, name);
  const redefine = (target, name, descriptor) => {
    try {
      Object.defineProperty(target, name, { configurable: true, enumerable: true, ...descriptor });
    } catch (error) {
      // A locked-down property keeps its native behaviour.
    }
  };

  // 1. Window offsets — own accessors on window in every current browser.
  for (const name of ['scrollY', 'pageYOffset']) {
    const native = own(window, name);
    if (!native || typeof native.get !== 'function') continue;
    redefine(window, name, {
      get() {
        const el = scroller();
        return el ? el.scrollTop : native.get.call(window);
      },
      set(value) {
        if (typeof native.set === 'function') native.set.call(window, value);
      },
    });
  }

  // 2. Element metrics on <html> and <body> — shadow the prototype accessors.
  const protoTop = own(Element.prototype, 'scrollTop');
  const protoHeight = own(Element.prototype, 'scrollHeight');
  for (const el of [document.documentElement, document.body]) {
    if (!el) continue;
    if (protoTop && typeof protoTop.get === 'function') {
      redefine(el, 'scrollTop', {
        get() {
          const s = scroller();
          return s ? s.scrollTop : protoTop.get.call(el);
        },
        set(value) {
          const s = scroller();
          if (s) {
            s.scrollTop = value;
          } else if (typeof protoTop.set === 'function') {
            protoTop.set.call(el, value);
          }
        },
      });
    }
    if (protoHeight && typeof protoHeight.get === 'function') {
      redefine(el, 'scrollHeight', {
        get() {
          const s = scroller();
          return s ? s.scrollHeight : protoHeight.get.call(el);
        },
      });
    }
  }

  // 3. Relay the wrapper's scroll events to document/window listeners.
  //    Element scroll events do not bubble; a document-level one does, so
  //    window listeners receive it exactly like a native page scroll.
  let relaying = false;
  const relay = () => {
    if (relaying || !scroller()) return;
    relaying = true;
    try {
      document.dispatchEvent(new Event('scroll', { bubbles: true }));
    } finally {
      relaying = false;
    }
  };
  const bind = () => {
    const el = wrapper();
    if (!el || el.__kiyoScrollBridgeBound) return;
    el.__kiyoScrollBridgeBound = true;
    el.addEventListener('scroll', relay, { passive: true });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
